/* "It saved" has to be said somewhere, and it must not be said with an alert().
 *
 * One live region for the whole app, so a screen reader hears a write land without
 * the focus moving. `role="status"` and not `role="alert"`: a save is a status, and
 * alert interrupts whatever is being read. */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';

export type ToastTone = 'good' | 'bad' | 'plain';
interface Toast { id: number; text: string; tone: ToastTone }

const Ctx = createContext<(text: string, tone?: ToastTone) => void>(() => {});

export function useToast(): (text: string, tone?: ToastTone) => void {
  return useContext(Ctx);
}

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const host = useRef<HTMLDivElement>(null);

  /* A MANUAL POPOVER, so it lives in the browser's TOP LAYER.
     A showModal() dialog is in that layer too, and nothing outside it can be painted
     above its backdrop whatever the z-index says. A save made inside a dialog has to
     be reported where the dialog is, and until this it was reported behind it: the
     refusal "say what makes it two-way" was rendered, correct, and invisible. */
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    try { el.showPopover(); } catch { /* older engines: it falls back to fixed */ }
  }, []);


  const push = useCallback((text: string, tone: ToastTone = 'plain') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    /* RE-SHOWN ON EVERY TOAST, not once on mount. The top layer is a stack in the
       order things entered it, so a dialog opened after the host sits above it and
       the toast is painted behind the backdrop. Hiding and showing moves the host
       back to the top, which is where a message about what just happened belongs. */
    const el = host.current;
    if (el) {
      try { el.hidePopover(); } catch { /* not open: nothing to hide */ }
      try { el.showPopover(); } catch { /* older engines: the fixed fallback */ }
    }
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);

  /* Memoised: the context value is the function, and a new function every render
     re-renders every screen that reads it. */
  const value = useMemo(() => push, [push]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toasts" ref={host} popover="manual" role="status" aria-live="polite">
        {/* A toast is a dark thing on a light page, so it takes the same scope the
            sidebar does rather than carrying two literal hexes. Tokens, inverted. */}
        {toasts.map((t) => (
          <div className={'toast ' + t.tone} key={t.id} data-surface="dark">
            <span className="t-body-small">{t.text}</span>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
